// src/utils/weatherCalculations.js

/**
 * Convert Celsius to Fahrenheit
 */
export const celsiusToFahrenheit = (celsius) => {
  return (celsius * 9/5) + 32;
};

/**
 * Convert Fahrenheit to Celsius
 */
export const fahrenheitToCelsius = (fahrenheit) => {
  return (fahrenheit - 32) * 5/9;
};

/**
 * Convert hPa to inHg
 */
export const hPaToInHg = (hPa) => {
  return hPa * 0.02953;
};

/**
 * Calculate Dew Point
 * @param {number} tempC - Temperature in Celsius
 * @param {number} rh - Relative Humidity (0-100)
 * @returns {number} Dew Point in Celsius
 */
export const calculateDewPoint = (tempC, rh) => {
  const a = 17.27;
  const b = 237.7;
  const alpha = ((a * tempC) / (b + tempC)) + Math.log(rh/100);
  return (b * alpha) / (a - alpha);
};

/**
 * Calculate Heat Index (Feels Like Temperature)
 * @param {number} tempC - Temperature in Celsius
 * @param {number} rh - Relative Humidity (0-100)
 * @returns {number} Heat Index in Celsius
 */
export const calculateHeatIndex = (tempC, rh) => {
  const tempF = celsiusToFahrenheit(tempC);
  
  // Rothfusz regression
  let hi = -42.379 + 
           2.04901523 * tempF + 
           10.14333127 * rh - 
           0.22475541 * tempF * rh - 
           6.83783e-3 * tempF ** 2 - 
           5.481717e-2 * rh ** 2 + 
           1.22874e-3 * tempF ** 2 * rh + 
           8.5282e-4 * tempF * rh ** 2 - 
           1.99e-6 * tempF ** 2 * rh ** 2;

  // Adjustment for low heat index values
  if (tempF < 80) {
    hi = tempF;
  }

  return fahrenheitToCelsius(hi);
};

/**
 * Calculate Wet Bulb Temperature
 * @param {number} tempC - Temperature in Celsius
 * @param {number} rh - Relative Humidity (0-100)
 * @param {number} pressureHPa - Pressure in hPa
 * @returns {number} Wet Bulb Temperature in Celsius
 */
export const calculateWetBulb = (tempC, rh, pressureHPa) => {
  // This is a simplified calculation - for more accuracy, you'd need an iterative approach
  const dewPoint = calculateDewPoint(tempC, rh);
  return (tempC * Math.atan(0.151977 * Math.sqrt(rh + 8.313659)) + 
          Math.atan(tempC + rh) - Math.atan(rh - 1.676331) + 
          0.00391838 * Math.pow(rh, 1.5) * Math.atan(0.023101 * rh) - 
          4.686035);
};

/**
 * Calculate Absolute Humidity
 * @param {number} tempC - Temperature in Celsius
 * @param {number} rh - Relative Humidity (0-100)
 * @returns {number} Absolute Humidity in g/m³
 */
export const calculateAbsoluteHumidity = (tempC, rh) => {
  const e = (rh/100) * 6.112 * Math.exp((17.67 * tempC)/(tempC + 243.5));
  return (2.167 * e)/(tempC + 273.15);
};

/**
 * Get comfort level description based on temperature and humidity
 * @param {number} tempC - Temperature in Celsius
 * @param {number} rh - Relative Humidity (0-100)
 * @returns {Object} Comfort level description and color
 */
export const getComfortLevel = (tempC, rh) => {
  const heatIndex = calculateHeatIndex(tempC, rh);
  
  if (heatIndex > 40) {
    return { level: 'Extreme Danger', color: '#ff0000' };
  } else if (heatIndex > 32) {
    return { level: 'Danger', color: '#ff4444' };
  } else if (heatIndex > 27) {
    return { level: 'Caution', color: '#ffaa00' };
  } else if (tempC < 10) {
    return { level: 'Cool', color: '#00aaff' };
  } else {
    return { level: 'Comfortable', color: '#00ff00' };
  }
};

/**
 * Format date for display
 * @param {string} dateString - ISO date string
 * @returns {string} Formatted date string
 */
export const formatDate = (dateString) => {
  return new Date(dateString).toLocaleString();
};

/**
 * Calculate air density
 * @param {number} tempC - Temperature in Celsius
 * @param {number} pressureHPa - Pressure in hPa
 * @param {number} rh - Relative Humidity (0-100)
 * @returns {number} Air density in kg/m³
 */
export const calculateAirDensity = (tempC, pressureHPa, rh) => {
  const tempK = tempC + 273.15;
  const pressurePa = pressureHPa * 100;
  const e = (rh/100) * 6.112 * Math.exp((17.67 * tempC)/(tempC + 243.5));
  return (pressurePa/(287.05 * tempK)) * (1 - (0.378 * e)/pressurePa);
};

/**
 * Generate statistics for a dataset
 * @param {Array} data - Array of numbers
 * @returns {Object} Statistics object
 */
export const calculateStats = (data) => {
  const numbers = data.filter(n => !isNaN(n));
  if (numbers.length === 0) return null;
  
  const sorted = [...numbers].sort((a, b) => a - b);
  const sum = sorted.reduce((a, b) => a + b, 0);
  const mean = sum / sorted.length;
  
  return {
    min: sorted[0],
    max: sorted[sorted.length - 1],
    mean: mean,
    median: sorted.length % 2 === 0 
      ? (sorted[sorted.length/2 - 1] + sorted[sorted.length/2]) / 2
      : sorted[Math.floor(sorted.length/2)],
    stdDev: Math.sqrt(
      sorted.reduce((acc, val) => acc + Math.pow(val - mean, 2), 0) / sorted.length
    )
  };
};